// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { format } from 'date-fns';

// Custom hover plugin to highlight the entire vertical column with filled rectangle
export const verticalHoverPlugin = {
    id: 'verticalHover',
    beforeDraw: (chart) => {
        if (chart.tooltip?.opacity === 0) return;
        
        const { ctx, tooltip, chartArea } = chart;
        if (tooltip.dataPoints?.length > 0) {
            const { x } = tooltip.dataPoints[0].element;
            
            // Save the canvas state
            ctx.save();
            
            // Set transparency and color for the hover highlight
            ctx.globalAlpha = 0.1;
            ctx.fillStyle = '#000000';
            
            // Calculate bar width (similar to the bar's width)
            const meta = chart.getDatasetMeta(0);
            const barWidth = meta.data[0] ? meta.data[0].width : 20;
            
            // Draw the vertical highlight rect
            ctx.fillRect(
                x - barWidth / 2,
                chartArea.top,
                barWidth,
                chartArea.bottom - chartArea.top
            );
            
            // Restore the canvas state
            ctx.restore();
        }
    }
};

// Get the tooltip title for the timeline chart (correctly format the dates based on the time interval and range)
export function getTooltipTitle(tooltipItems, timeframe, timeIntervalMinutes) {
    const startDate = new Date(tooltipItems[0].parsed.x);
    const endDate = new Date(tooltipItems[0].parsed.x + timeIntervalMinutes * 60 * 1000);

    const daysDifference = Math.ceil((timeframe.endDate - timeframe.startDate) / (1000 * 60 * 60 * 24));

    if (timeIntervalMinutes >= 1440) {
        // Only daily data points - show the date
        return format(startDate, 'dd MMM yyyy');
    }
    
    const startDateString = format(startDate, 'HH:mm');
    const endDateString = format(endDate, 'HH:mm');
    
    if (daysDifference > 1.5) {
        // When spanning multiple days, show the date and the time span
        return format(startDate, 'dd MMM yyyy') + ` | ${startDateString} - ${endDateString}`;	
    } else {
        // When spanning a single day, show the only thetime span
        return `${startDateString} - ${endDateString}`;
    }
}

// Get the time axis format for the timeline chart (correctly format the dates based on the time range)
export function getTimeAxisFormat(timeframe) {
    const daysDifference = Math.ceil((timeframe.endDate - timeframe.startDate) / (1000 * 60 * 60 * 24));

    return {
        unit: daysDifference > 3 ? 'day' : 'hour',
        displayFormats: {
            hour: 'HH:mm',
            day: 'dd MMM'
        },
        tooltipFormat: daysDifference > 3 ? 'dd MMM yyyy' : 'HH:mm'
    }
}